import axios from 'axios';
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  User,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  CheckEmailResponse,
  UserDevice,
} from '../types/auth';
import { useAuthStore } from '../hooks/useAuthStore';
import toast from 'react-hot-toast';

import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate instance for file uploads with longer timeout
export const fileUploadClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutes, large video uploads on a slow connection need real headroom
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Store reference to auth store
interface AuthStore {
  getState(): {
    refreshAuthToken: () => Promise<void>;
    logout: () => void;
  };
}

interface ApiError {
  config?: {
    _retry?: boolean;
    headers?: { Authorization?: string };
    [key: string]: unknown;
  };
  response?: {
    status?: number;
  };
}

let authStore: AuthStore | null = null;

export const setAuthStore = (store: AuthStore) => {
  authStore = store;
};

// Add auth token (and optional org-scope header for SUPER_ADMIN) to requests
const addAuthToRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const state = useAuthStore.getState();
  if (state?.token) {
    config.headers.Authorization = `Bearer ${state.token}`;
  }

  // SUPER_ADMIN/Root-org: if the user has scoped to a specific org, send the header.
  // We check both role and isRootOrg flag to match the OrgSelector's criteria.
  const user = state?.user;
  const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.isRootOrg;

  if (isSuperAdmin && state?.scopedOrgId) {
    // Check if the header was explicitly set to null (e.g. by dashboardService using globalConfig)
    // to indicate a request that MUST remain platform-wide.
    // Axios normalizes headers but we check common variations.
    const hasExplicitGlobalRequest =
      config.headers['X-Organisation-Id'] === null ||
      config.headers['x-organisation-id'] === null ||
      (config.headers as any)['X-Organisation-Id'] === null ||
      (config.headers as any)['x-organisation-id'] === null;

    if (!hasExplicitGlobalRequest) {
      config.headers['X-Organisation-Id'] = String(state.scopedOrgId);
    } else {
      // It's a global request. Remove the null placeholder so it's not sent as a string.
      delete config.headers['X-Organisation-Id'];
      delete config.headers['x-organisation-id'];
    }
  }

  return config;
};

// Request interceptor for main client
apiClient.interceptors.request.use(
  (config) => {
    return addAuthToRequest(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for file upload client
fileUploadClient.interceptors.request.use(
  (config) => {
    return addAuthToRequest(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Guards against a redirect+toast firing once per failed request when a
// locked page fires several requests at once (e.g. a dashboard loading
// multiple widgets), only the first 402 in a burst should act.
let redirectingForExpiredPlan = false;

const PLAN_LOCKED_ALLOWED_PATH = '/settings/plan';

// Response interceptor to handle auth errors
const handleAuthError = async (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return Promise.reject(error);
  }

  const err = error as ApiError;
  const originalRequest = err.config;

  // Add null checks
  if (!originalRequest) {
    return Promise.reject(error);
  }

  const isAuthEndpoint = /\/auth\/(login|register|refresh)/.test(originalRequest.url ?? '');
  if (err.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
    originalRequest._retry = true;

    try {
      if (authStore) {
        await authStore.getState().refreshAuthToken();

        const token = useAuthStore.getState()?.token;
        if (token) {
          // Ensure headers object exists
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest as AxiosRequestConfig); // Type assertion for axios config
        }
      }
    } catch (refreshError) {
      if (authStore) {
        authStore.getState().logout();
      } else {
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    }
  }

  // Plan expired/free trial ended (PlanActiveGuard, backend), every route
  // except billing (already @SkipPlanCheck-exempt, so never lands here) and
  // whatever the Settings > My Plan page itself calls returns 402. Send the
  // user there instead of leaving every other widget on the page showing a
  // silent failed-request state.
  // A lapsed subscription past its grace period is read-only, not locked out: a blocked write just
  // explains itself and leaves the user where they are.
  const isWrite = ['post', 'put', 'patch', 'delete'].includes(String(originalRequest.method ?? '').toLowerCase());
  if (err.response?.status === 402 && isWrite) {
    toast.error('Your subscription has expired, the account is read-only until you renew.', { id: 'plan-read-only' });
  } else if (
    err.response?.status === 402 &&
    !redirectingForExpiredPlan &&
    window.location.pathname !== PLAN_LOCKED_ALLOWED_PATH
  ) {
    redirectingForExpiredPlan = true;
    toast.error('Your plan has expired. Upgrade to keep full access.');
    window.location.href = PLAN_LOCKED_ALLOWED_PATH;
  }

  return Promise.reject(error);
};

apiClient.interceptors.response.use((response) => response, handleAuthError);
fileUploadClient.interceptors.response.use((response) => response, handleAuthError);

// Auth API
export const authApi = {
  checkEmail: async (email: string): Promise<CheckEmailResponse> => {
    const response: AxiosResponse<CheckEmailResponse> = await apiClient.post('/auth/check-email', {
      email,
    });
    return response.data;
  },

  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  refresh: async (refreshData: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    const response: AxiosResponse<RefreshTokenResponse> = await apiClient.post(
      '/auth/refresh',
      refreshData
    );
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<User> = await apiClient.get('/user/data');
    return response.data;
  },

  logout: async (): Promise<void> => {
    // No backend logout endpoint, token invalidation is handled client-side
  },

  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    const response: AxiosResponse<TwoFactorSetupResponse> = await apiClient.post('/auth/2fa/setup');
    return response.data;
  },

  verify2FA: async (data: TwoFactorVerifyRequest): Promise<void> => {
    await apiClient.post('/auth/2fa/verify', data);
  },

  disable2FA: async (): Promise<void> => {
    await apiClient.post('/auth/2fa/disable');
  },

  sendEmailOtp: async (): Promise<void> => {
    await apiClient.post('/auth/2fa/email/send');
  },

  listDevices: async (): Promise<UserDevice[]> => {
    const response: AxiosResponse<UserDevice[]> = await apiClient.get('/auth/devices');
    return response.data;
  },

  approveDevice: async (id: number): Promise<void> => {
    await apiClient.post(`/auth/devices/${id}/approve`);
  },

  revokeDevice: async (id: number): Promise<void> => {
    await apiClient.delete(`/auth/devices/${id}`);
  },
};

export default apiClient;
